import { pluralRu } from './plural'
import { APP_TIME_ZONE, momentZone, zonedParts, zonedTime } from './tz'

// Календарная математика на native Date/Intl (конвенция экосистемы — без
// date-библиотеки). Date здесь всегда момент; календарные поля берутся через
// zonedParts, обратно — через zonedTime. На API уходит UTC ISO. Неделя
// начинается с понедельника.
//
// Зона у каждой функции — необязательный последний аргумент, и ДЕФОЛТ у него
// говорит, чей это вопрос. Календарные СУТКИ («сегодня», границы недели и
// месяца, событие на весь день, ISO-дата) считаются в зоне ОРГАНИЗАЦИИ: они
// общие для всех, и разъехавшись, ломают общий день. МОМЕНТ (время суток,
// подпись «14:00», разбор набранного человеком времени) считается в зоне
// УСТРОЙСТВА: он принадлежит тому, кто на него смотрит и кто его вводит.
//
// Смешивать половины одного значения нельзя: дата и время суток ОДНОГО момента
// обязаны читаться в одной зоне, иначе обратная сборка промахивается на сутки.
// Явный аргумент перебивает дефолт — им пользуется страница брони, строящая
// сетку в зоне ВЛАДЕЛЬЦА ссылки.

export function startOfDay(d: Date, tz: string = APP_TIME_ZONE): Date {
  const p = zonedParts(d, tz)
  return zonedTime({ year: p.year, month: p.month, day: p.day }, tz)
}

export function startOfWeek(d: Date, tz: string = APP_TIME_ZONE): Date {
  const day = startOfDay(d, tz)
  // Понедельник — начало недели; weekdayOf(): 0 = воскресенье
  const shift = (weekdayOf(day, tz) + 6) % 7
  return addDays(day, -shift, tz)
}

export function startOfMonth(d: Date, tz: string = APP_TIME_ZONE): Date {
  const p = zonedParts(d, tz)
  return zonedTime({ year: p.year, month: p.month, day: 1 }, tz)
}

/**
 * Сдвиг на календарные сутки, а не на 24 часа: в сутки перевода стрелок между
 * соседними полуночами 23 или 25 часов, но день — следующий.
 */
export function addDays(d: Date, n: number, tz: string = APP_TIME_ZONE): Date {
  const p = zonedParts(d, tz)
  return zonedTime(
    { year: p.year, month: p.month, day: p.day + n, hour: p.hour, minute: p.minute },
    tz,
  )
}

export function addMonths(d: Date, n: number, tz: string = APP_TIME_ZONE): Date {
  // День фиксируем 1-м: якорь навигации по месяцам, а не «то же число»
  const p = zonedParts(d, tz)
  return zonedTime({ year: p.year, month: p.month + n, day: 1 }, tz)
}

export function isSameDay(a: Date, b: Date, tz: string = APP_TIME_ZONE): boolean {
  const x = zonedParts(a, tz)
  const y = zonedParts(b, tz)
  return x.year === y.year && x.month === y.month && x.day === y.day
}

export function isToday(d: Date, tz: string = APP_TIME_ZONE): boolean {
  return isSameDay(d, new Date(), tz)
}

export function minutesSinceMidnight(d: Date, tz: string = momentZone()): number {
  const p = zonedParts(d, tz)
  return p.hour * 60 + p.minute
}

/** День недели даты в зоне (0 = воскресенье); неявный getDay() запрещён. */
export function weekdayOf(d: Date, tz: string = APP_TIME_ZONE): number {
  const p = zonedParts(d, tz)
  return new Date(Date.UTC(p.year, p.month - 1, p.day)).getUTCDay()
}

/** Момент целого часа `hour` тех суток, в которые попадает `d`, в зоне. */
export function atHour(d: Date, hour: number, tz: string = APP_TIME_ZONE): Date {
  const p = zonedParts(d, tz)
  return zonedTime({ year: p.year, month: p.month, day: p.day, hour, minute: 0 }, tz)
}

/** Число месяца в зоне — подпись ячейки сетки. */
export function dayOfMonth(d: Date, tz: string = APP_TIME_ZONE): number {
  return zonedParts(d, tz).day
}

/** Целых календарных дней от `a` до `b` (по датам, не по миллисекундам). */
export function diffDays(a: Date, b: Date, tz: string = APP_TIME_ZONE): number {
  const x = zonedParts(a, tz)
  const y = zonedParts(b, tz)
  const ms = Date.UTC(y.year, y.month - 1, y.day) - Date.UTC(x.year, x.month - 1, x.day)
  return Math.round(ms / 86_400_000)
}

export function weekDays(anchor: Date, tz: string = APP_TIME_ZONE): Date[] {
  const start = startOfWeek(anchor, tz)
  return Array.from({ length: 7 }, (_, i) => addDays(start, i, tz))
}

export interface MonthCell {
  date: Date
  outside: boolean
}

/** Сетка месяца: полные недели пн–вс, покрывающие месяц (28–42 ячейки). */
export function monthGrid(anchor: Date, tz: string = APP_TIME_ZONE): MonthCell[] {
  const first = startOfMonth(anchor, tz)
  const month = zonedParts(first, tz).month
  const last = addDays(addMonths(first, 1, tz), -1, tz)
  const gridStart = startOfWeek(first, tz)
  const gridEnd = addDays(startOfWeek(last, tz), 7, tz)
  const cells: MonthCell[] = []
  for (let d = gridStart; d < gridEnd; d = addDays(d, 1, tz)) {
    cells.push({ date: d, outside: zonedParts(d, tz).month !== month })
  }
  return cells
}

export function toISODate(d: Date, tz: string = APP_TIME_ZONE): string {
  const p = zonedParts(d, tz)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

/** `YYYY-MM-DD` → полночь этого дня в зоне; мусор → null (для validateSearch). */
export function fromISODate(s: string, tz: string = APP_TIME_ZONE): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return null
  const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const d = zonedTime({ year, month, day }, tz)
  // Date перекатывает out-of-range компоненты (13-й месяц → январь) — сверяем
  return toISODate(d, tz) === s ? d : null
}

/** Момент по дню `YYYY-MM-DD` и времени суток `HH:MM` в зоне. */
export function fromISODateTime(
  date: string,
  time: string,
  tz: string = momentZone(),
): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const t = /^(\d{2}):(\d{2})$/.exec(time)
  if (!d || !t) return null
  return zonedTime(
    {
      year: Number(d[1]),
      month: Number(d[2]),
      day: Number(d[3]),
      hour: Number(t[1]),
      minute: Number(t[2]),
    },
    tz,
  )
}

/** `HH:MM` для input[type=time] — настенное время момента в зоне. */
export function toTimeInput(d: Date, tz: string = momentZone()): string {
  const p = zonedParts(d, tz)
  return `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`
}

/**
 * All-day события бэк нормализует на целые сутки зоны организации (end
 * эксклюзивен). Возвращает первый и последний занятый день — рендер all-day
 * идёт по дням, не по миллисекундам.
 */
export function allDayRange(
  startAt: Date,
  endAt: Date,
  tz: string = APP_TIME_ZONE,
): { first: Date; last: Date } {
  const first = startOfDay(startAt, tz)
  return { first, last: addDays(startOfDay(endAt, tz), -1, tz) }
}

/** Занимает ли событие данный день зоны (учитывая all-day семантику). */
export function coversDay(
  ev: { startAt: Date; endAt: Date; allDay: boolean },
  day: Date,
  tz: string = APP_TIME_ZONE,
): boolean {
  if (ev.allDay) {
    const { first, last } = allDayRange(ev.startAt, ev.endAt, tz)
    return day >= first && day <= last
  }
  const dayStart = startOfDay(day, tz)
  const dayEnd = addDays(dayStart, 1, tz)
  return ev.startAt < dayEnd && ev.endAt > dayStart
}

// Кэшированные Intl-форматтеры: создание — дорогое, вызовов — сотни на рендер.
// Ключ включает зону — форматтер не привязан к одной: моменты идут в зоне
// устройства, сутки в зоне организации, бронь в зоне владельца ссылки.
type FormatterKind = 'time' | 'day' | 'dayShort' | 'monthTitle' | 'dayMonth' | 'fullDate'

const formatterOptions: Record<FormatterKind, Intl.DateTimeFormatOptions> = {
  time: { hour: '2-digit', minute: '2-digit' },
  day: { weekday: 'short', day: 'numeric', month: 'long' },
  dayShort: { weekday: 'short', day: 'numeric' },
  monthTitle: { month: 'long', year: 'numeric' },
  dayMonth: { day: 'numeric', month: 'long' },
  fullDate: { day: 'numeric', month: 'long', year: 'numeric' },
}

const formatters = new Map<string, Intl.DateTimeFormat>()

function fmt(kind: FormatterKind, tz: string): Intl.DateTimeFormat {
  const key = `${kind}|${tz}`
  let f = formatters.get(key)
  if (!f) {
    f = new Intl.DateTimeFormat('ru', { ...formatterOptions[kind], timeZone: tz })
    formatters.set(key, f)
  }
  return f
}

export function fmtTime(d: Date, tz: string = momentZone()): string {
  return fmt('time', tz).format(d)
}

/** `пн, 20 июля` */
export function fmtDay(d: Date, tz: string = APP_TIME_ZONE): string {
  return fmt('day', tz).format(d)
}

/** `пн 20` — колонки недели/матрицы team */
export function fmtDayShort(d: Date, tz: string = APP_TIME_ZONE): string {
  return fmt('dayShort', tz).format(d)
}

/** `июль 2026` (Intl отдаёт «июль 2026 г.» — хвост оставляем, это норма ru) */
export function fmtMonthTitle(d: Date, tz: string = APP_TIME_ZONE): string {
  return fmt('monthTitle', tz).format(startOfMonth(d, tz))
}

/** `14–20 июля 2026` / `28 июля – 3 августа 2026` — заголовок недели */
export function fmtWeekTitle(anchor: Date, tz: string = APP_TIME_ZONE): string {
  const days = weekDays(anchor, tz)
  const first = days[0] as Date
  const last = days[6] as Date
  const a = zonedParts(first, tz)
  const b = zonedParts(last, tz)
  if (a.month === b.month) {
    return `${a.day}–${b.day} ${monthNameOf(last, tz)} ${b.year}`
  }
  return `${fmt('dayMonth', tz).format(first)} – ${fmt('dayMonth', tz).format(last)} ${b.year}`
}

function monthNameOf(d: Date, tz: string): string {
  return fmt('dayMonth', tz).format(d).replace(/^\d+ /, '')
}

/**
 * Интервал события для peek/списков:
 * - timed в один день: `пн, 20 июля · 14:00–15:00`
 * - timed через дни:   `20 июля 14:00 – 21 июля 10:00`
 * - all-day один день: `пн, 20 июля · весь день`
 * - all-day интервал:  `20–22 июля · весь день`
 */
export function fmtEventRange(
  ev: { startAt: Date; endAt: Date; allDay: boolean },
  tz: string = momentZone(),
): string {
  if (ev.allDay) {
    // Сутки события на весь день общие для всех — они и читаются в зоне
    // организации, а не в зоне смотрящего: иначе «весь день» съезжает на день.
    const org = APP_TIME_ZONE
    const { first, last } = allDayRange(ev.startAt, ev.endAt, org)
    if (isSameDay(first, last, org)) return `${fmtDay(first, org)} · весь день`
    const a = zonedParts(first, org)
    const b = zonedParts(last, org)
    if (a.month === b.month) {
      return `${a.day}–${b.day} ${monthNameOf(last, org)} · весь день`
    }
    return `${fmt('dayMonth', org).format(first)} – ${fmt('dayMonth', org).format(last)} · весь день`
  }
  if (isSameDay(ev.startAt, ev.endAt, tz)) {
    return `${fmtDay(ev.startAt, tz)} · ${fmtTime(ev.startAt, tz)}–${fmtTime(ev.endAt, tz)}`
  }
  return `${fmt('dayMonth', tz).format(ev.startAt)} ${fmtTime(ev.startAt, tz)} – ${fmt('dayMonth', tz).format(ev.endAt)} ${fmtTime(ev.endAt, tz)}`
}

/** `20 июля 2026` — подписи вне календарной сетки */
export function fmtFullDate(d: Date, tz: string = APP_TIME_ZONE): string {
  return fmt('fullDate', tz).format(d)
}

const HOUR_MS = 3_600_000
const MINUTE_MS = 60_000
const DAY_MS = 24 * HOUR_MS

/**
 * Давность записи ленты: `только что` / `12 мин назад` / `3 ч назад` /
 * `2 дня назад`. Старше недели — датой (`4 августа`): «31 день назад» человек
 * всё равно пересчитывает в дату.
 */
export function fmtAgo(d: Date, now: Date = new Date(), tz: string = momentZone()): string {
  const ms = now.getTime() - d.getTime()
  if (ms < MINUTE_MS) return 'только что'
  if (ms < HOUR_MS) return `${Math.floor(ms / MINUTE_MS)} мин назад`
  if (ms < DAY_MS) return `${Math.floor(ms / HOUR_MS)} ч назад`
  const days = Math.floor(ms / DAY_MS)
  if (days < 7) return `${days} ${pluralRu(days, 'день', 'дня', 'дней')} назад`
  return fmt('dayMonth', tz).format(d)
}

/**
 * Продолжительность события: `45 мин` / `1 ч 30 мин` / `3 дня`. Зоны не
 * спрашивает: у timed-события это разность моментов, одинаковая в любой зоне, а
 * all-day считается календарными сутками организации — так он и хранится (целые
 * сутки, конец эксклюзивен), поэтому перевод стрелок его не укорачивает.
 */
export function fmtDuration(ev: { startAt: Date; endAt: Date; allDay: boolean }): string {
  if (ev.allDay) {
    const days = Math.max(1, diffDays(ev.startAt, ev.endAt, APP_TIME_ZONE))
    return `${days} ${pluralRu(days, 'день', 'дня', 'дней')}`
  }
  const minutes = Math.round(Math.max(0, ev.endAt.getTime() - ev.startAt.getTime()) / MINUTE_MS)
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} мин`
  if (rest === 0) return `${hours} ч`
  return `${hours} ч ${rest} мин`
}
